import { useEffect, useMemo, useRef } from 'react'
import { Pendant } from '../../../types/index';
import { renderNecklace } from '../../../utils/matterjs/render-necklace';
import { updatePendants } from '../../../utils/matterjs/update-pendants';
import { createEngine, createRender } from '../../../utils/matterjs';
import { run } from '../../../utils/matterjs/run';
import { useApp } from '../../../hooks/useApp';
import { updateColor } from '../../../utils/matterjs/update-color';
import Matter from 'matter-js';
import classNames from 'classnames';
import { getActiveLengthByMainProductVariantId, getActiveMaterialByMainProductVariantId } from '../../../utils';
import { renderBracelet } from '../../../utils/matterjs/render-bracelet';

interface Props {
  className?: string
} 

export const Preview = ({ className }: Props) => { 
  const { 
    mainProduct,
    activeMainProductVariantId,
    activePendantProductVariantIds, 
  } = useApp()

  const activeMaterial = getActiveMaterialByMainProductVariantId(mainProduct, activeMainProductVariantId)
  const activeLength = getActiveLengthByMainProductVariantId(mainProduct, activeMainProductVariantId)

  const isFirstRenderRef = useRef(true)
  const renderRef = useRef<Matter.Render>()
  const materialRef = useRef<string>(activeMaterial)
  const lengthRef = useRef<string>(activeLength)
  const pendantProductVariantsRef = useRef<Pendant[]>(activePendantProductVariantIds)

  // TODO: Create & use the following hooks: useMatter, useNecklace & useBracelet
  const engine = useMemo(createEngine, [])
  const canvasRef = useRef<HTMLCanvasElement>(null)     

  useEffect(() => {
    return () => {
      if (!renderRef.current) return 

      Matter.Render.stop(renderRef.current)
      Matter.World.clear(engine.world, false)
      Matter.Engine.clear(engine)
      renderRef.current.canvas.remove()
    }
  }, [])

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
      materialRef.current = activeMaterial
      lengthRef.current = activeLength
      renderRef.current = createRender(engine, canvasRef.current!)

      run(engine, renderRef.current)

      mainProduct.metafields.type === 'Necklace'
        ? renderNecklace(engine, renderRef.current!, mainProduct, activeMainProductVariantId, [])
        : renderBracelet(engine, renderRef.current!, mainProduct, activeMainProductVariantId, [])

      updatePendants(
        [],
        activePendantProductVariantIds, 
        engine,
        mainProduct,
        activeMainProductVariantId,
      )
      return
    }

    if (lengthRef.current !== activeLength) {
      mainProduct.metafields.type === 'Necklace'
        ? renderNecklace(engine, renderRef.current!, mainProduct, activeMainProductVariantId, [])
        : renderBracelet(engine, renderRef.current!, mainProduct, activeMainProductVariantId, [])

      updatePendants(
        [],
        activePendantProductVariantIds, 
        engine,
        mainProduct,
        activeMainProductVariantId,
      )
      lengthRef.current = activeLength
      
      return
    }

    if (materialRef.current !== activeMaterial) {
      updateColor(engine, mainProduct, activeMainProductVariantId)

      materialRef.current = activeMaterial
    }

    if (pendantProductVariantsRef.current !== activePendantProductVariantIds) {
      updatePendants(pendantProductVariantsRef.current, activePendantProductVariantIds, engine, mainProduct,  activeMainProductVariantId)
      pendantProductVariantsRef.current = activePendantProductVariantIds
    }
  }, [activeMainProductVariantId, activePendantProductVariantIds])

  return (
    <canvas 
      ref={canvasRef}
      className={classNames("rounded-sm", className)}
      width="800"
      height="800"
    />
  )
}